import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ExtensionSelectionModal from '../../components/feature/ExtensionSelectionModal';
import SEO from '../../components/feature/SEO';

export default function RegistrationComplete() {
  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);

  // Open the Extension Selection Modal
  const openExtensionModal = () => {
    setIsExtensionModalOpen(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <SEO title="Registration Complete – QBOVerifier" description="Your QBOVerifier registration is complete. Follow the steps to connect QuickBooks and start protecting your invoices." noIndex />
      <Header />

      <main className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-28 h-28 mb-8">
              <div
                className={`inline-flex items-center justify-center w-28 h-28 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full shadow-xl transition-transform duration-500 ease-in-out ${
                  animate ? 'scale-animation' : ''
                }`}
                style={{
                  animation: animate ? 'pulseScale 0.6s ease-in-out' : 'none',
                }}
              >
                <i className="ri-check-double-line text-6xl text-white"></i>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Registration Completed
            </h1>

            <p className="text-xl md:text-2xl text-gray-700">
              Welcome and thank you, we do appreciate your business.
            </p>
          </div>

          {/* Appreciation Banner */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-8 mb-10 text-center text-white shadow-lg">
            <i className="ri-trophy-line text-4xl mb-4"></i>
            <h2 className="text-2xl font-bold mb-2">You're All Set!</h2>
            <p className="text-teal-100 mx-auto text-2xl">
              <span className="whitespace-nowrap">
                Your registration is complete and your account is ready.
              </span>
              <br />
              Next Steps below.
            </p>
          </div>

          {/* Next Steps Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 mb-10 border border-gray-100">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#DBEAFE' }}>
                  <i className="ri-checkbox-circle-line text-2xl" style={{ color: '#2563EB' }}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Step 1 - Connect your QBO account to QBOVerifier
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    The quickest way to connect QBOVerifier to your QuickBooks Online account is to{' '}
                    <a
                      href="https://bio-mail.com/webhooks/quickbooks/public/connect.asp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline cursor-pointer"
                      style={{ color: '#29B3E6' }}
                    >
                      Connect QBOVerifier
                    </a>{' '}
                    via our automatic connection page. Just click the{' '}
                    <a
                      href="https://bio-mail.com/webhooks/quickbooks/public/connect.asp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline cursor-pointer"
                      style={{ color: '#29B3E6' }}
                    >
                      Link
                    </a>.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100"></div>

              {/* Step 2 */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#DBEAFE' }}>
                  <i className="ri-checkbox-circle-line text-2xl" style={{ color: '#2563EB' }}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Step 2 - Install the Browser Extension.
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Now simply head over to our{' '}
                    <button
                      onClick={openExtensionModal}
                      className="font-semibold underline cursor-pointer"
                      style={{ color: '#29B3E6' }}
                    >
                      Install Extension page
                    </button>{' '}
                    and select the <strong>Business Extension</strong>. Select your Accounting
                    Platform and follow the prompts.<br />
                    The browser extension (for Chrome or Firefox) is part of the secret sauce that
                    displays and sends your selected QBOVerifier Invoice layout.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100"></div>

              {/* Step 3 */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#DBEAFE' }}>
                  <i className="ri-checkbox-circle-line text-2xl" style={{ color: '#2563EB' }}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Step 3 - Go ahead and start using QBOVerifier.
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Log in to your QuickBooks Online account. Create an invoice, and when you select
                    the "Save and send" button, you should see the popup Invoice Preview screen.
                    <br />
                    Click "Send" at the top right. This activates the QBOVerifier back-end process
                    that sends your formatted invoice.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100"></div>

              {/* Step 4 */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#DBEAFE' }}>
                  <i className="ri-checkbox-circle-line text-2xl" style={{ color: '#2563EB' }}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    View the Invoice Email and Verification popup.
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your customers will automatically see the QBOVerifier Stamp when they open your
                    invoice email.<br />
                    This will immediately assure them it is a legitimate email from you.<br />
                    There are two ways they can definitely confirm the email has not been tampered
                    with and is original.<br />
                    <br />
                    <span className="block pl-6" style={{ textIndent: '-1.5rem' }}>
                      a) They can install the absolutely free, Consumer Extension, that is also
                      available from the Install Extension button on our website.
                      The Browser Extension has inbuilt real‑time integrity checking and is the
                      preferred option.
                    </span>
                    <br />
                    <span className="block pl-6" style={{ textIndent: '-1.5rem' }}>
                      b) Alternatively, our proprietary QBOVerifier mobile app can also be used and
                      will also display the same QBOVerifier Identity popup.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Welcome Message Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 mb-10 border border-gray-100">
            <div className="space-y-8">
              {/* We Value Your Business */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                  <i className="ri-heart-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    We Value Your Business
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your trust in QBOVerifier means the world to us. We are committed to providing
                    you with the highest level of service and security for your invoice verification
                    needs. Every customer is important to us, and we look forward to being a valuable
                    partner in protecting your business.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100"></div>

              {/* Support Section */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-customer-service-2-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    We're Here to Support You
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Should you ever need assistance, our dedicated support team is ready to help.
                    Simply head over to our{' '}
                    <button
                      onClick={() => navigate('/contact')}
                      className="font-semibold underline cursor-pointer"
                      style={{ color: '#29B3E6' }}
                    >
                      Contact page
                    </button>{' '}
                    and select the <strong>Support</strong> option. We're committed to ensuring
                    your experience with QBOVerifier is smooth and successful.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100"></div>

              {/* Suggestions */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-lightbulb-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Your Ideas Matter
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We welcome your suggestions and feature requests! Your feedback helps us improve
                    and build a better product for everyone. If you have ideas on how we can serve
                    you better, we'd love to hear from you. Together, we can make QBOVerifier even
                    more powerful.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Return Home Button */}
          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-10 py-4 text-white rounded-xl font-semibold text-lg transition-all hover:shadow-xl cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#29B3E6' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E9BD1')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#29B3E6')}
            >
              <i className="ri-home-line mr-3 text-xl"></i>
              Return to Home
            </button>
          </div>

          {/* Additional Help */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-500 mb-3">
              Have questions? We're happy to help.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="font-semibold hover:underline cursor-pointer whitespace-nowrap"
              style={{ color: '#29B3E6' }}
            >
              Contact Our Team
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Extension Selection Modal */}
      <ExtensionSelectionModal
        isOpen={isExtensionModalOpen}
        onClose={() => setIsExtensionModalOpen(false)}
      />
    </div>
  );
}